import React, { useMemo, useState } from 'react';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import BulkActionsToolbar from 'shared/components/scope_tree/bulk_actions_toolbar';
import ScopeFamilySection from 'shared/components/scope_tree/scope_family_section';
import {
  allScopeNames,
  withAdded,
  withRemoved,
} from 'shared/components/scope_tree/selection';
import { ScopeFamily } from 'shared/components/scope_tree/types';
import ConsentAction from './consent_action';
import ConsentHeader from './consent_header';
import LoggedInAs from './logged_in_as';
import type { LoggedInAsProps } from './logged_in_as';

const t = withLocalePrefix('authorization.authorization_form');

type Props = {
  name: string;
  families: ScopeFamily[];
  accountSettingsUrl: string;
  companyLogoUrl?: string | null;
  denyUrl: string;
  clientIdHost?: string;
  redirectUriHost?: string;
  loopbackOnly?: boolean;
  loggedInAs: LoggedInAsProps;
  identityScopes?: string[];
};

export default function PublicClientConsentForm({
  name,
  families,
  accountSettingsUrl,
  companyLogoUrl,
  denyUrl,
  clientIdHost,
  redirectUriHost,
  loopbackOnly,
  loggedInAs,
  identityScopes = [],
}: Props) {
  const allNames = useMemo(() => allScopeNames(families), [families]);
  const [selected, setSelected] = useState(() => new Set(allNames));

  const setScopes = (names: string[], checked: boolean) => {
    setSelected((prev) =>
      checked ? withAdded(prev, names) : withRemoved(prev, names)
    );
  };

  const toggleScope = (scopeName: string) => {
    setScopes([scopeName], !selected.has(scopeName));
  };

  const onCancel = () => {
    window.location.href = denyUrl;
  };

  return (
    <Stack>
      <ConsentHeader
        name={name}
        companyLogoUrl={companyLogoUrl}
        clientIdHost={clientIdHost}
        redirectUriHost={redirectUriHost}
        loopbackOnly={loopbackOnly}
      />
      <LoggedInAs {...loggedInAs} />
      <InformationalCallOut bodyText={t('beta_callout')} />
      {identityScopes.length > 0 && (
        <InformationalCallOut bodyText={t('identity_notice', { name })} />
      )}
      <Stack spacing={2}>
        {families.length === 0 ? (
          <InformationalCallOut bodyText={t('no_consentable_scopes')} />
        ) : (
          <>
            <Body>{t('scope_description')}</Body>
            <input
              type="hidden"
              name="scope"
              value={[...selected, ...identityScopes].join(' ')}
            />
            <BulkActionsToolbar families={families} onSetScopes={setScopes} />
            {families.map((family) => (
              <ScopeFamilySection
                key={family.key}
                family={family}
                selected={selected}
                onToggleScope={toggleScope}
                onSetScopes={setScopes}
              />
            ))}
          </>
        )}
        <ConsentAction
          accountSettingsUrl={accountSettingsUrl}
          onCancel={onCancel}
          disabled={selected.size === 0}
          submitLabel={
            families.length > 0
              ? t('authorize_cta', {
                  selected: selected.size,
                  total: allNames.length,
                })
              : undefined
          }
        />
      </Stack>
    </Stack>
  );
}
